import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { createTRPCReact } from '@trpc/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { AppRouter } from '@ecom/api/routers';
import { routeTree } from './lib/routeTree.gen';
import './styles.css';

// ─── tRPC client ──────────────────────────────────────────────────────────────
export const trpc = createTRPCReact<AppRouter>();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, error: any) => {
        // Don't retry on auth or business errors.
        if (error?.data?.code === 'UNAUTHORIZED') return false;
        if (error?.data?.code === 'FORBIDDEN') return false;
        if (error?.data?.code === 'NOT_FOUND') return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * Resolve API base URL.
 * - In dev (Vite proxy): relative '/api/trpc' routes through vite.config.ts proxy.
 * - In prod (Pages → Workers): use the absolute Workers URL via Vite env.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';

const trpcClient = trpc.createClient({
  links: [
    trpc.httpBatchLink({
      url: `${API_BASE}/api/trpc`,
      headers: () => ({ 'x-trpc-source': 'react-app' }),
      fetch: (url, options) => fetch(url, { ...options, credentials: 'include' }),
    }),
  ],
});

// ─── Router ───────────────────────────────────────────────────────────────────
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient, trpcClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// ─── Mount ────────────────────────────────────────────────────────────────────
const root = document.getElementById('root');
if (!root) throw new Error('#root not found');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        <RouterProvider router={router} />
        {import.meta.env.DEV && <ReactQueryDevtools />}
      </trpc.Provider>
    </QueryClientProvider>
  </StrictMode>,
);
